import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { formatDistanceToNow } from "date-fns";
import useWorkspaceNavigation from "../../hooks/useWorkspaceNavigation";
import { NotificationSummaryDto } from "../../shared/dtos/notification/NotificationSummaryDto";
import { NotificationResponseDto } from "../../shared/dtos/notification/NotificationResponseDto";

interface NotificationUserCardProps {
  notification: NotificationSummaryDto | NotificationResponseDto;
  onRead: (id: string) => void;
}

const NotificationUserCard: React.FC<NotificationUserCardProps> = ({
  notification,
  onRead,
}) => {
  const theme = useTheme();
  const { navigateToTab } = useWorkspaceNavigation();

  const handleAction = () => {
    if (!notification.isRead) onRead(notification.id);

    // Polymorphic Routing Logic
    if (notification.relatedEntityId && notification.relatedContext) {
      switch (notification.relatedContext) {
        case "LEDGER":
          navigateToTab(`/student-fee-ledgers/${notification.relatedEntityId}`);
          break;
        case "REPORT_CARD":
          navigateToTab(
            `/student-term-reports/${notification.relatedEntityId}`,
          );
          break;
        default:
          break;
      }
    }
  };

  return (
    <Box
      onClick={handleAction}
      sx={{
        p: 2,
        cursor: "pointer",
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: notification.isRead
          ? "transparent"
          : alpha(theme.palette.secondary.main, 0.05),
        "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.08) },
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      {!notification.isRead && (
        <CircleIcon sx={{ fontSize: 10, mt: 0.8, color: "secondary.main" }} />
      )}
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: notification.isRead ? 500 : 700 }}
        >
          {notification.subject}
        </Typography>

        {notification.subject && (
          <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
            {notification.subject}
          </Typography>
        )}

        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          {formatDistanceToNow(new Date(notification.createdAt), {
            addSuffix: true,
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default NotificationUserCard;
